import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Slider,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  LockOutlined,
  UndoOutlined,
  UnlockOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import * as api from 'util/api';
import {
  isKegSku,
  priceFieldGreyOutTooltip,
} from 'util/pricing-fields';
import {
  createDefaultSkuValues,
  generateMatrixSkuName,
  stripProductSkuShortLabel,
} from 'util/sku-formatter';
import {
  MARGIN_TARGET_GUIDE_RANGES,
  CAMEL_TO_SNAKE_PRICE,
  GST_MULT,
  kegVolumeMlFromSku,
  parseCaseUnits,
  resolveTargetPct,
} from '../marginUtils';
import {
  isHighAbvOrWild,
  parseAbv,
} from '../kegPricingSuggestions';
import {
  KEG_COST_EDITOR_FIELDS,
  KEG_PRODUCTION_DEFAULTS,
  computeKegProductionCost,
  ourGpPct,
} from '../kegProductionCost';
import {
  PACKAGED_COST_EDITOR_FIELDS,
  PACKAGED_PRODUCTION_DEFAULTS,
  computePackagedProductionCost,
  packagedFormatKey,
  resolvePackagedFormat,
  resolvePackagingComponents,
} from '../packagedProductionCost';
import { slabCaseDiscount } from '../pricingSuggestions';
import {
  EXAMPLE_FORMAT_PRESET_GROUPS,
  EXAMPLE_FORMAT_PRESETS,
  buildExampleSku,
  solveExamplePrices,
  exampleFieldGreyed,
  exampleFieldMargin,
  onPremCaseUplift,
  guideTypeFromProduct,
  isActiveSku,
  skuHasFormat,
  findSkuByFormat,
  persistableFieldGroups,
  currentPriceNote,
  changedPrices,
  roundApplyPricesTo50c,
} from '../pricingExampleSolver';
import {
  downloadCsv,
  isoDateLocal,
  parseMixFromCsv,
  serializeMixToCsv,
} from './pricingMixCsv';
import './PricingExample.less';

const { Text } = Typography;

const TYPE_OPTIONS = [
  { value: '', label: 'Standard' },
  { value: 'Wild', label: 'Wild' },
  { value: 'Clean Barrel', label: 'Clean Barrel' },
  { value: 'Other', label: 'Other' },
];

const PACKAGED_GROUPS = [
  {
    title: 'NZ trade (ex GST)',
    fields: [
      { key: 'priceOff', label: 'Off Prem' },
      { key: 'priceOn', label: 'On Prem' },
      { key: 'priceProgressive', label: 'Progressive' },
      { key: 'corporate', label: 'Corporate' },
    ],
  },
  {
    title: 'RRP (inc GST)',
    fields: [
      { key: 'rrpUnit', label: 'RRP Unit' },
      { key: 'rrpPack', label: 'RRP Pack' },
      { key: 'rrpCase', label: 'RRP Case' },
    ],
  },
  {
    title: 'On-premise (inc GST)',
    fields: [
      { key: 'progressiveUnit', label: 'Prog. retail unit' },
      { key: 'progressivePack', label: 'Prog. retail pack' },
      { key: 'priceBar', label: 'O.P.' },
    ],
  },
];

const KEG_GROUPS = [
  {
    title: 'NZ trade (ex GST)',
    fields: [
      { key: 'priceOn', label: 'On Prem' },
    ],
  },
  {
    title: 'Venue (inc GST)',
    fields: [
      { key: 'priceVenuePerLitre', label: 'Venue/L' },
      { key: 'pricePint', label: 'Pint (440ml)' },
      { key: 'priceHalfPint', label: 'Half Pint (285ml)' },
      { key: 'price150ml', label: '150ml Pour' },
    ],
  },
];

function groupsForSku(sku) {
  return isKegSku(sku) ? KEG_GROUPS : PACKAGED_GROUPS;
}

function fieldInExampleGroups(sku, fieldKey) {
  return groupsForSku(sku).some((group) => (
    group.fields.some((field) => field.key === fieldKey)
  ));
}

function lockAppliesToSku(sku, fieldKey) {
  return Boolean(
    fieldKey
    && !exampleFieldGreyed(sku, fieldKey)
    && fieldInExampleGroups(sku, fieldKey),
  );
}

const SLIDER_RANGE_BY_FIELD = {
  priceOff: [40, 200],
  priceOn: [40, 200],
  priceProgressive: [40, 200],
  corporate: [40, 200],
  rrpCase: [40, 220],
  rrpPack: [10, 40],
  progressivePack: [0, 40],
  rrpUnit: [3, 70],
  priceBar: [5, 70],
  progressiveUnit: [0, 40],
  priceVenuePerLitre: [10, 50],
  pricePint: [8, 25],
  priceHalfPint: [6, 20],
  price150ml: [4, 20],
};

const KEG_PRICE_ON_RANGE = [100, 600];
const KEG_DEFAULT_PRICE_ON = 380;

const COST_L_INPUT = /^\d*[.]?\d*$/;

function formatMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  const v = Number(n);
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

function costPartRow(label, value) {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return { label, value: Number(value) };
}

/** Same wastage bake as kegCost / caseCost: rate / (1 - wastage) * litres. */
function volumeWithWastage(rate, litres, wastagePct) {
  const r = Number(rate);
  const vol = Number(litres);
  if (!Number.isFinite(r) || !Number.isFinite(vol)) return null;
  const frac = Number(wastagePct) / 100;
  if (!Number.isFinite(frac) || frac < 0 || frac >= 1) return r * vol;
  return (r / (1 - frac)) * vol;
}

/** Cost breakdown parts. Hidden when Cost/L is empty. */
function skuCostBreakdownParts(kegProduction, packagedProduction) {
  if (kegProduction) {
    const { breakdown: b, kegCost } = kegProduction;
    const { kegSizeLiters: litres } = b;
    return [
      costPartRow('Cost', kegCost),
      costPartRow('Ingredients', volumeWithWastage(
        b.costPerFinishedL, litres, b.wastagePct,
      )),
      costPartRow('Wages', volumeWithWastage(
        b.wagesPerLitre, litres, b.wastagePct,
      )),
      costPartRow('Excise', b.excise),
      costPartRow('Packaging', b.kegLease),
      costPartRow('3PL', b.threePl),
    ].filter(Boolean);
  }
  if (packagedProduction) {
    const { breakdown: b, caseCost } = packagedProduction;
    const { litres } = b;
    return [
      costPartRow('Cost', caseCost),
      costPartRow('Ingredients', volumeWithWastage(
        b.costPerFinishedL, litres, b.wastagePct,
      )),
      costPartRow('Wages', volumeWithWastage(
        b.wagesPerLitre, litres, b.wastagePct,
      )),
      costPartRow('Excise', b.excise),
      costPartRow('Packaging', b.packaging),
      costPartRow('3PL', b.threePl),
    ].filter(Boolean);
  }
  return [];
}

function buildSkuCostBreakdown(kegProduction, packagedProduction) {
  const parts = skuCostBreakdownParts(kegProduction, packagedProduction);
  if (!parts.length) return null;
  return parts.map((p) => `${p.label} $${formatMoney(p.value)}`).join(' | ');
}

function productAbv(product) {
  const n = parseAbv(product?.abv ?? product?.abvMin ?? product?.abv_min);
  return n != null ? n : 5;
}

function skuDisplayName(sku, productName) {
  if (sku?.name) {
    return stripProductSkuShortLabel(sku.name, productName) || sku.name;
  }
  return generateMatrixSkuName({
    containerType: sku.containerType ?? sku.container_type,
    unitType: sku.unitType ?? sku.unit_type,
    packType: sku.packType ?? sku.pack_type,
    caseType: sku.caseType ?? sku.case_type,
  }) || `SKU ${sku.key}`;
}

function lockPageTextSelect() {
  const { body } = document;
  if (body.dataset.exampleSliderDrag) return;
  body.dataset.exampleSliderDrag = '1';
  const prev = body.style.userSelect;
  body.style.userSelect = 'none';
  const unlock = () => {
    body.style.userSelect = prev;
    delete body.dataset.exampleSliderDrag;
    window.removeEventListener('mouseup', unlock);
    window.removeEventListener('touchend', unlock);
  };
  window.addEventListener('mouseup', unlock);
  window.addEventListener('touchend', unlock);
}

function sliderRange(fieldKey, value, keg) {
  const fallback = [0, 200];
  const base = keg && fieldKey === 'priceOn'
    ? KEG_PRICE_ON_RANGE
    : (SLIDER_RANGE_BY_FIELD[fieldKey] || fallback);
  let min = base[0];
  let max = base[1];
  const n = Number(value);
  if (value != null && !Number.isNaN(n)) {
    if (n < min) min = Math.floor(n);
    if (n > max) max = Math.ceil(n);
  }
  return { min, max };
}

const PACKAGED_OUR_GP_FIELDS = new Set([
  'rrpUnit', 'rrpPack', 'rrpCase', 'priceBar',
]);

/** Brewery GP on inc-GST retail vs allocated case production cost. */
function packagedOurGpPct(fieldKey, sku, prices, packagedProduction) {
  if (!packagedProduction || isKegSku(sku)) return null;
  if (!PACKAGED_OUR_GP_FIELDS.has(fieldKey)) return null;
  const sellInc = Number(prices?.[fieldKey]);
  if (!Number.isFinite(sellInc) || sellInc <= 0) return null;
  const { caseCost } = packagedProduction;
  if (caseCost == null || !Number.isFinite(caseCost)) return null;
  const parsed = parseCaseUnits(sku?.caseType ?? sku?.case_type);
  if (!parsed) return null;
  let costEx;
  if (fieldKey === 'rrpCase') {
    costEx = caseCost;
  } else if (fieldKey === 'rrpPack') {
    if (parsed.casePacks <= 0) return null;
    costEx = caseCost / parsed.casePacks;
  } else if (parsed.totalUnits <= 0) {
    return null;
  } else {
    costEx = caseCost / parsed.totalUnits;
  }
  return ourGpPct(sellInc / GST_MULT, costEx);
}

function packagedOurGpNote(fieldKey, sku, prices, packagedProduction) {
  const gp = packagedOurGpPct(fieldKey, sku, prices, packagedProduction);
  if (gp == null) return null;
  return `our GP ${formatMoney(gp)}%`;
}

function priceMargins({
  fieldKey, sku, prices, kegProduction, packagedProduction,
}) {
  const tradeGp = exampleFieldMargin(fieldKey, sku, prices);
  let ourGp = packagedOurGpPct(fieldKey, sku, prices, packagedProduction);
  if (ourGp == null && fieldKey === 'priceOff' && packagedProduction) {
    ourGp = ourGpPct(prices?.priceOff, packagedProduction.caseCost);
  }
  if (ourGp == null && fieldKey === 'priceOn') {
    if (isKegSku(sku) && kegProduction) {
      ourGp = ourGpPct(prices?.priceOn, kegProduction.kegCost);
    } else if (!isKegSku(sku) && packagedProduction) {
      ourGp = ourGpPct(prices?.priceOn, packagedProduction.caseCost);
    }
  }
  return { tradeGp, ourGp };
}

function isKegFormatKey(formatKey) {
  const preset = EXAMPLE_FORMAT_PRESETS.find((p) => p.key === formatKey);
  return preset?.values?.containerType === 'keg';
}

function fieldNote({
  fieldKey, sku, prices, targetMap, rules, abv, skipped,
  kegProduction, packagedProduction,
}) {
  if (skipped?.[fieldKey]) return skipped[fieldKey];
  if (fieldKey === 'corporate') return 'On Prem × 1.1';
  if (fieldKey === 'priceOff' && packagedProduction) {
    const gp = ourGpPct(prices?.priceOff, packagedProduction.caseCost);
    const bits = [`cost $${formatMoney(packagedProduction.caseCost)}`];
    if (gp != null) bits.push(`our GP ${formatMoney(gp)}%`);
    return bits.join(' · ');
  }
  if (fieldKey === 'priceOn' && isKegSku(sku) && kegProduction) {
    const gp = ourGpPct(prices?.priceOn, kegProduction.kegCost);
    const bits = [`cost $${formatMoney(kegProduction.kegCost)}`];
    if (gp != null) bits.push(`our GP ${formatMoney(gp)}%`);
    return bits.join(' · ');
  }
  if (fieldKey === 'priceOn' && !isKegSku(sku) && prices?.priceOff != null) {
    const uplift = onPremCaseUplift(sku, rules);
    const bits = [`Off Prem +$${formatMoney(uplift)}`];
    if (packagedProduction) {
      const gp = ourGpPct(prices?.priceOn, packagedProduction.caseCost);
      if (gp != null) bits.push(`our GP ${formatMoney(gp)}%`);
    }
    return bits.join(' · ');
  }
  if (fieldKey === 'priceProgressive') return 'Off Prem × 1.0265';
  if (fieldKey === 'progressiveUnit') return 'RRP Unit × markup';
  const ourGp = packagedOurGpNote(fieldKey, sku, prices, packagedProduction);
  if (fieldKey === 'priceBar') return ourGp;
  if (fieldKey === 'rrpCase') {
    const parsed = parseCaseUnits(sku?.caseType ?? sku?.case_type);
    const bits = [];
    if (parsed?.totalUnits > 0) {
      const disc = slabCaseDiscount(parsed.totalUnits, rules);
      const keep = Math.round((1 - disc) * 1000) / 1000;
      bits.push(`RRP Unit × ${parsed.totalUnits} × ${keep}`);
    }
    if (ourGp) bits.push(ourGp);
    return bits.length ? bits.join(' · ') : null;
  }
  const camel = fieldKey;
  const snake = CAMEL_TO_SNAKE_PRICE[camel];
  if (!snake) return null;
  const high = isHighAbvOrWild(abv, sku?.productType, rules);
  const cond = high && (
    camel === 'priceVenuePerLitre'
    || camel === 'pricePint'
    || camel === 'priceHalfPint'
    || camel === 'price150ml'
  ) ? 'high_abv_or_wild' : '';
  const target = resolveTargetPct(targetMap, sku?.productRange, camel, cond);
  const actual = exampleFieldMargin(camel, sku, prices);
  const bits = [];
  if (target != null) bits.push(`target ${formatMoney(target)}%`);
  if (actual != null) bits.push(`GP ${formatMoney(actual)}%`);
  if (ourGp) bits.push(ourGp);
  if (cond) bits.push('strong / Wild');
  return bits.length ? bits.join(' · ') : null;
}

function applyFieldNote(fieldKey, sku, prices) {
  const actual = exampleFieldMargin(fieldKey, sku, prices);
  if (actual == null) return null;
  return `GP ${formatMoney(actual)}%`;
}

function withCurrentNote(note, existingSku, fieldKey) {
  const current = existingSku ? currentPriceNote(existingSku, fieldKey) : null;
  return [note, current].filter(Boolean).join(' ') || null;
}

function PlaygroundBlock({
  title, extra, children, className,
}) {
  const cls = ['pricing-example-block', className].filter(Boolean).join(' ');
  return (
    <section className={cls}>
      <div className="pricing-example-block-head">
        <Text className="pricing-example-block-title">{title}</Text>
        {extra}
      </div>
      {children}
    </section>
  );
}

PlaygroundBlock.propTypes = {
  title: PropTypes.string.isRequired,
  extra: PropTypes.node,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};

PlaygroundBlock.defaultProps = {
  extra: null,
  className: undefined,
};

function defaultCostRate(fieldKey, sku) {
  if (isKegSku(sku)) return KEG_PRODUCTION_DEFAULTS[fieldKey];
  if (fieldKey === 'wastagePct') {
    return resolvePackagedFormat(sku)?.wastagePct;
  }
  if (Object.prototype.hasOwnProperty.call(PACKAGED_PRODUCTION_DEFAULTS, fieldKey)) {
    return PACKAGED_PRODUCTION_DEFAULTS[fieldKey];
  }
  return KEG_PRODUCTION_DEFAULTS[fieldKey];
}

function costEditorFields(sku) {
  return isKegSku(sku) ? KEG_COST_EDITOR_FIELDS : PACKAGED_COST_EDITOR_FIELDS;
}

function CostRateRow({
  field, value, overridden, onChange, onReset,
}) {
  const pct = field.kind === 'pct';
  return (
    <div
      className={[
        'pricing-example-cost-row',
        overridden ? 'pricing-example-cost-row-over' : '',
      ].filter(Boolean).join(' ')}
    >
      <Text className="pricing-example-cost-row-label">{field.label}</Text>
      <InputNumber
        size="small"
        min={0}
        max={pct ? 99.9 : 999}
        step={field.step}
        value={value}
        onChange={onChange}
        formatter={(v) => {
          if (v === '' || v == null) return '';
          return pct ? `${formatMoney(v)}%` : `$${formatMoney(v)}`;
        }}
        parser={(v) => String(v).replace(/[%$]/g, '')}
      />
      {overridden ? (
        <Tooltip title="Reset">
          <button
            type="button"
            className="pricing-example-cost-reset"
            onClick={onReset}
            aria-label={`Reset ${field.label}`}
          >
            <UndoOutlined />
          </button>
        </Tooltip>
      ) : (
        <span className="pricing-example-cost-reset-spacer" />
      )}
    </div>
  );
}

CostRateRow.propTypes = {
  field: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    kind: PropTypes.string.isRequired,
    step: PropTypes.number.isRequired,
  }).isRequired,
  value: PropTypes.number,
  overridden: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

CostRateRow.defaultProps = {
  value: undefined,
};

function PackagingRow({ component, onChange, onReset }) {
  return (
    <div
      className={[
        'pricing-example-cost-row',
        'pricing-example-cost-pack-row',
        component.overridden ? 'pricing-example-cost-row-over' : '',
      ].filter(Boolean).join(' ')}
    >
      <Text className="pricing-example-cost-row-label">{component.label}</Text>
      <InputNumber
        size="small"
        min={0}
        max={99}
        step={0.01}
        value={component.rate}
        onChange={onChange}
        formatter={(v) => {
          if (v === '' || v == null) return '';
          return `$${formatMoney(v)}`;
        }}
        parser={(v) => String(v).replace(/\$/g, '')}
      />
      <Text type="secondary" className="pricing-example-cost-pack-math">
        {`× ${component.qty} = $${formatMoney(component.total)}`}
      </Text>
      {component.overridden ? (
        <Tooltip title="Reset">
          <button
            type="button"
            className="pricing-example-cost-reset"
            onClick={onReset}
            aria-label={`Reset ${component.label}`}
          >
            <UndoOutlined />
          </button>
        </Tooltip>
      ) : (
        <span className="pricing-example-cost-reset-spacer" />
      )}
    </div>
  );
}

PackagingRow.propTypes = {
  component: PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    rate: PropTypes.number.isRequired,
    qty: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    overridden: PropTypes.bool.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
};

function CostEditor({
  sku,
  costOverrides,
  packagingOverrides,
  onRateChange,
  onRateReset,
  onPackagingChange,
  onPackagingReset,
  onResetAll,
  hasOverrides,
}) {
  const fields = costEditorFields(sku);
  const format = isKegSku(sku) ? null : resolvePackagedFormat(sku);
  const formatKey = packagedFormatKey(sku);
  const packMap = packagingOverrides[formatKey] || {};
  const components = format
    ? resolvePackagingComponents(format.packagingComponents, packMap)
    : [];

  return (
    <div className="pricing-example-cost-editor">
      {fields.map((field) => {
        const fallback = defaultCostRate(field.key, sku);
        const overridden = Object.prototype.hasOwnProperty.call(
          costOverrides,
          field.key,
        );
        const value = overridden ? costOverrides[field.key] : fallback;
        return (
          <CostRateRow
            key={field.key}
            field={field}
            value={value}
            overridden={overridden}
            onChange={(v) => onRateChange(field.key, v)}
            onReset={() => onRateReset(field.key)}
          />
        );
      })}
      {components.length > 0 && (
        <div className="pricing-example-cost-pack">
          <Text className="pricing-example-cost-pack-title">Packaging</Text>
          {components.map((component) => (
            <PackagingRow
              key={component.key}
              component={component}
              onChange={(v) => onPackagingChange(formatKey, component.key, v)}
              onReset={() => onPackagingReset(formatKey, component.key)}
            />
          ))}
        </div>
      )}
      {hasOverrides && (
        <Button size="small" type="link" onClick={onResetAll}>
          Reset all
        </Button>
      )}
    </div>
  );
}

CostEditor.propTypes = {
  sku: PropTypes.shape({
    containerType: PropTypes.string,
    unitType: PropTypes.string,
    packType: PropTypes.string,
    caseType: PropTypes.string,
  }).isRequired,
  costOverrides: PropTypes.objectOf(PropTypes.number).isRequired,
  packagingOverrides: PropTypes.objectOf(
    PropTypes.objectOf(PropTypes.number),
  ).isRequired,
  onRateChange: PropTypes.func.isRequired,
  onRateReset: PropTypes.func.isRequired,
  onPackagingChange: PropTypes.func.isRequired,
  onPackagingReset: PropTypes.func.isRequired,
  onResetAll: PropTypes.func.isRequired,
  hasOverrides: PropTypes.bool.isRequired,
};

const PricingExample = ({ targetMap, rules }) => {
  const [formatKey, setFormatKey] = useState('15x440');
  const [abv, setAbv] = useState(5);
  const [range, setRange] = useState('Core');
  const [productType, setProductType] = useState('');
  const [anchorField, setAnchorField] = useState('priceOff');
  const [anchorValue, setAnchorValue] = useState(90);
  const [lockedField, setLockedField] = useState(null);
  const [lockedValue, setLockedValue] = useState(null);
  const [costPerLitre, setCostPerLitre] = useState(null);
  const [costPerLitreText, setCostPerLitreText] = useState('');
  const [costOverrides, setCostOverrides] = useState({});
  const [packagingOverrides, setPackagingOverrides] = useState({});

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [beerSkus, setBeerSkus] = useState([]);
  const [skusLoading, setSkusLoading] = useState(false);

  const [applyOpen, setApplyOpen] = useState(false);
  const [applyPrices, setApplyPrices] = useState({});
  const [applying, setApplying] = useState(false);
  const skuLoadIdRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    setProductsLoading(true);
    api.getProducts()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        setProducts(list);
      })
      .finally(() => {
        if (!cancelled) setProductsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const selectedProduct = useMemo(
    () => products.find((p) => String(p.id) === String(selectedProductId)) || null,
    [products, selectedProductId],
  );

  const loadBeerSkus = useCallback(async (productId) => {
    const loadId = skuLoadIdRef.current + 1;
    skuLoadIdRef.current = loadId;
    if (!productId) {
      setBeerSkus([]);
      setSkusLoading(false);
      return;
    }
    setSkusLoading(true);
    try {
      const data = await api.getProductSkus(productId);
      if (skuLoadIdRef.current !== loadId) return;
      const list = Array.isArray(data) ? data.filter(isActiveSku) : [];
      setBeerSkus(list);
    } catch {
      if (skuLoadIdRef.current !== loadId) return;
      setBeerSkus([]);
    } finally {
      if (skuLoadIdRef.current === loadId) setSkusLoading(false);
    }
  }, []);

  const clearBeer = () => {
    setSelectedProductId(null);
    setAbv(5);
    setProductType('');
    setCostPerLitre(null);
    setCostPerLitreText('');
    setApplyOpen(false);
    loadBeerSkus(null);
  };

  const onBeerChange = (productId) => {
    if (!productId) {
      clearBeer();
      return;
    }
    const product = products.find((p) => String(p.id) === String(productId));
    setSelectedProductId(productId);
    setAbv(productAbv(product));
    setProductType(guideTypeFromProduct(product?.type));
    setCostPerLitre(null);
    setCostPerLitreText('');
    setBeerSkus([]);
    setApplyOpen(false);
    loadBeerSkus(productId);
  };

  const productOptions = useMemo(() => (
    products
      .filter((p) => p.name)
      .map((p) => ({ label: p.name, value: p.id }))
  ), [products]);

  const preset = EXAMPLE_FORMAT_PRESETS.find((p) => p.key === formatKey)
    || EXAMPLE_FORMAT_PRESETS[0];

  const sku = useMemo(() => buildExampleSku({
    ...preset.values,
    productRange: range,
    productType,
  }), [preset, range, productType]);

  const solved = useMemo(() => solveExamplePrices({
    sku,
    anchorField,
    anchorValue,
    lockedField,
    lockedValue,
    targetMap,
    rules,
    abv,
  }), [sku, anchorField, anchorValue, lockedField, lockedValue, targetMap, rules, abv]);

  useEffect(() => {
    if (!lockedField) return;
    if (!lockAppliesToSku(sku, lockedField) || solved.skipped[lockedField]) {
      setLockedField(null);
      setLockedValue(null);
    }
  }, [lockedField, sku, solved.skipped]);

  const skuFormatKey = packagedFormatKey(sku);
  const productionSettings = useMemo(() => ({
    ...costOverrides,
    packagingComponents: packagingOverrides[skuFormatKey],
  }), [costOverrides, packagingOverrides, skuFormatKey]);

  const hasCostOverrides = useMemo(() => {
    if (Object.keys(costOverrides).length) return true;
    return Object.keys(packagingOverrides).some((key) => (
      Object.keys(packagingOverrides[key] || {}).length > 0
    ));
  }, [costOverrides, packagingOverrides]);

  const kegProduction = useMemo(() => {
    if (!isKegSku(sku)) return null;
    return computeKegProductionCost({
      costPerFinishedL: costPerLitre,
      abvPct: abv,
      kegSizeLiters: kegVolumeMlFromSku(sku) / 1000,
      settings: productionSettings,
    });
  }, [sku, costPerLitre, abv, productionSettings]);

  const packagedProduction = useMemo(() => {
    if (isKegSku(sku)) return null;
    return computePackagedProductionCost({
      costPerFinishedL: costPerLitre,
      abvPct: abv,
      sku,
      settings: productionSettings,
    });
  }, [sku, costPerLitre, abv, productionSettings]);

  const costBreakdown = useMemo(
    () => buildSkuCostBreakdown(kegProduction, packagedProduction),
    [kegProduction, packagedProduction],
  );

  const groups = isKegSku(sku) ? KEG_GROUPS : PACKAGED_GROUPS;
  const applyGroups = useMemo(
    () => persistableFieldGroups(groups),
    [groups],
  );

  const existingFormatKeys = useMemo(() => {
    const keys = new Set();
    EXAMPLE_FORMAT_PRESETS.forEach((p) => {
      if (skuHasFormat(beerSkus, p.values)) keys.add(p.key);
    });
    return keys;
  }, [beerSkus]);

  const existingSku = useMemo(
    () => findSkuByFormat(beerSkus, preset.values),
    [beerSkus, preset],
  );

  const applyPreset = (next) => {
    const nextSku = buildExampleSku({
      ...next.values,
      productRange: range,
      productType,
    });
    setFormatKey(next.key);
    // Keep ABV / prices across format changes until page refresh.
    // Packaged → keg: On Prem seeds $380 (not leftover Off $90).
    const nextIsKeg = isKegSku(nextSku);
    const prevIsKeg = isKegSku(sku);
    if (nextIsKeg && !prevIsKeg) {
      setAnchorField('priceOn');
      setAnchorValue(KEG_DEFAULT_PRICE_ON);
      if (lockedField === 'priceOn') {
        setLockedValue(KEG_DEFAULT_PRICE_ON);
      }
    } else if (exampleFieldGreyed(nextSku, anchorField)) {
      setAnchorField(nextIsKeg ? 'priceOn' : 'priceOff');
    }
    if (lockedField && !lockAppliesToSku(nextSku, lockedField)) {
      setLockedField(null);
      setLockedValue(null);
    }
  };

  const onFieldChange = (fieldKey, value) => {
    setAnchorField(fieldKey);
    setAnchorValue(value);
    if (lockedField === fieldKey) {
      setLockedValue(value);
    }
  };

  const toggleLock = (fieldKey, value) => {
    if (lockedField === fieldKey) {
      setLockedField(null);
      setLockedValue(null);
      return;
    }
    const n = Number(value);
    if (value == null || Number.isNaN(n) || n <= 0) return;
    setLockedField(fieldKey);
    setLockedValue(n);
  };

  const onCostRateChange = (key, value) => {
    if (value == null || value === '') return;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return;
    if ((key === 'wastagePct' || key === 'grossMarginPct') && n >= 100) {
      return;
    }
    setCostOverrides((prev) => ({ ...prev, [key]: n }));
  };

  const onCostRateReset = (key) => {
    setCostOverrides((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const onPackagingRateChange = (formatId, componentKey, value) => {
    if (value == null || value === '') return;
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) return;
    setPackagingOverrides((prev) => ({
      ...prev,
      [formatId]: {
        ...(prev[formatId] || {}),
        [componentKey]: n,
      },
    }));
  };

  const onPackagingRateReset = (formatId, componentKey) => {
    setPackagingOverrides((prev) => {
      const next = { ...prev };
      const current = { ...(next[formatId] || {}) };
      delete current[componentKey];
      if (Object.keys(current).length) {
        next[formatId] = current;
      } else {
        delete next[formatId];
      }
      return next;
    });
  };

  const resetAllCostOverrides = () => {
    setCostOverrides({});
    setPackagingOverrides({});
  };

  const onCostPerLitreChange = (event) => {
    const text = String(event.target.value).replace(/\$/g, '');
    if (text !== '' && !COST_L_INPUT.test(text)) return;
    setCostPerLitreText(text);
    if (text === '' || text === '.') {
      setCostPerLitre(null);
      return;
    }
    const value = Number(text);
    if (!Number.isFinite(value) || value < 0) return;
    setCostPerLitre(value);
    if (isKegSku(sku)) {
      const prod = computeKegProductionCost({
        costPerFinishedL: value,
        abvPct: abv,
        kegSizeLiters: kegVolumeMlFromSku(sku) / 1000,
        settings: productionSettings,
      });
      if (!prod) return;
      setAnchorField('priceOn');
      setAnchorValue(Math.round(prod.kegSell * 100) / 100);
      return;
    }
    const prod = computePackagedProductionCost({
      costPerFinishedL: value,
      abvPct: abv,
      sku,
      settings: productionSettings,
    });
    if (!prod) return;
    setAnchorField('priceOff');
    setAnchorValue(Math.round(prod.caseSell * 100) / 100);
  };

  const openApply = () => {
    const next = {};
    applyGroups.forEach((group) => {
      group.fields.forEach((field) => {
        if (exampleFieldGreyed(sku, field.key)) return;
        if (solved.skipped[field.key]) return;
        const value = solved.prices[field.key];
        if (value != null && !Number.isNaN(Number(value))) {
          next[field.key] = Number(value);
        }
      });
    });
    setApplyPrices(next);
    setApplyOpen(true);
  };

  const confirmApply = async () => {
    if (!selectedProduct || skusLoading) return;
    setApplying(true);
    try {
      const name = generateMatrixSkuName({
        productName: selectedProduct.name,
        ...preset.values,
      });
      if (existingSku) {
        const payload = changedPrices(applyPrices, existingSku);
        if (!Object.keys(payload).length) {
          message.info('No price changes');
          setApplyOpen(false);
          return;
        }
        await api.updateProductSkuStrict(
          selectedProduct.id,
          existingSku.key,
          payload,
        );
        setApplyOpen(false);
        await loadBeerSkus(selectedProduct.id);
        Modal.success({
          title: 'Prices updated',
          content: existingSku.name || name,
        });
        return;
      }
      const payload = {
        ...createDefaultSkuValues({
          productId: selectedProduct.id,
          productMondayId: selectedProduct.mondayId,
        }),
        name,
        ...preset.values,
        ...applyPrices,
      };
      await api.createProductSku(selectedProduct.id, payload);
      setApplyOpen(false);
      await loadBeerSkus(selectedProduct.id);
      Modal.success({
        title: 'SKU created',
        content: name,
      });
    } catch (err) {
      message.error(err?.message || 'Failed to apply prices');
    } finally {
      setApplying(false);
    }
  };

  const applyDisabled = !selectedProduct || skusLoading;
  let applyTip;
  if (skusLoading) {
    applyTip = 'Loading SKUs…';
  } else if (!selectedProduct) {
    applyTip = 'Select a beer first';
  }

  const applyMix = (mix) => {
    const { productId } = mix;
    if (productId) {
      const product = products.find((p) => String(p.id) === String(productId));
      if (product) {
        setSelectedProductId(product.id);
        loadBeerSkus(product.id);
      } else {
        setSelectedProductId(null);
        setBeerSkus([]);
        message.warning(
          mix.productName
            ? `Beer "${mix.productName}" not found`
            : 'Saved beer not found',
        );
      }
    } else {
      setSelectedProductId(null);
      setBeerSkus([]);
    }
    setApplyOpen(false);

    const nextFormat = mix.formatKey;
    const keg = isKegFormatKey(nextFormat);
    setFormatKey(nextFormat);
    setAbv(mix.abv ?? 5);
    setRange(mix.range ?? 'Core');
    setProductType(mix.productType ?? '');
    let nextAnchorField = mix.anchorField;
    if (!nextAnchorField && keg) {
      nextAnchorField = 'priceOn';
    } else if (!nextAnchorField) {
      nextAnchorField = 'priceOff';
    }
    let nextAnchorValue = mix.anchorValue;
    if (nextAnchorValue == null && keg) {
      nextAnchorValue = KEG_DEFAULT_PRICE_ON;
    } else if (nextAnchorValue == null) {
      nextAnchorValue = 90;
    }
    setAnchorField(nextAnchorField);
    setAnchorValue(nextAnchorValue);
    setLockedField(mix.lockedField || null);
    setLockedValue(mix.lockedValue ?? null);
    const cost = mix.costPerLitre;
    setCostPerLitre(cost == null ? null : cost);
    setCostPerLitreText(cost == null ? '' : String(cost));
    setCostOverrides(mix.costOverrides || {});
    setPackagingOverrides(mix.packagingOverrides || {});
  };

  const exportMix = () => {
    const mix = {
      formatKey,
      abv,
      range,
      productType,
      anchorField,
      anchorValue,
      lockedField,
      lockedValue,
      costPerLitre,
      productId: selectedProductId,
      productName: selectedProduct?.name || '',
      costOverrides,
      packagingOverrides,
    };
    const priceGroups = groups
      .map((group) => ({
        title: group.title,
        rows: group.fields.reduce((rows, field) => {
          if (exampleFieldGreyed(sku, field.key)) return rows;
          if (solved.skipped[field.key]) return rows;
          const value = solved.prices[field.key];
          if (value == null || Number.isNaN(Number(value))) return rows;
          const { tradeGp, ourGp } = priceMargins({
            fieldKey: field.key,
            sku,
            prices: solved.prices,
            kegProduction,
            packagedProduction,
          });
          rows.push({
            label: field.label,
            value: Number(value),
            tradeGp,
            ourGp,
          });
          return rows;
        }, []),
      }))
      .filter((group) => group.rows.length);
    const costRows = skuCostBreakdownParts(kegProduction, packagedProduction);
    const csv = serializeMixToCsv(mix, { costRows, priceGroups });
    downloadCsv(`pricing-mix-${formatKey}-${isoDateLocal()}.csv`, csv);
  };

  const importMixFile = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      const { mix, errors } = parseMixFromCsv(String(reader.result || ''));
      if (!mix.formatKey) {
        message.error(errors[0] || 'Not a playground mix CSV');
        return;
      }
      applyMix(mix);
      if (errors.length) {
        message.warning(errors.join('; '));
        return;
      }
      message.success('Mix imported');
    };
    reader.onerror = () => {
      message.error('Could not read CSV');
    };
    reader.readAsText(file);
  };

  const applyRow = (
    <div className="pricing-example-row pricing-example-apply-row">
      <div className="pricing-example-label">
        <Text className="pricing-example-label-text">{'\u00a0'}</Text>
      </div>
      <Tooltip title={applyTip}>
        <span className="pricing-example-apply-wrap">
          <Button
            type="primary"
            block
            disabled={applyDisabled}
            onClick={openApply}
          >
            Apply…
          </Button>
        </span>
      </Tooltip>
      <Text type="secondary" className="pricing-example-note">
        {'\u00a0'}
      </Text>
    </div>
  );

  return (
    <Card
      size="small"
      title="Pricing Playground"
      className="pricing-guide-section pricing-example"
      extra={(
        <div className="pricing-example-mix-actions">
          <Upload
            accept=".csv,text/csv"
            showUploadList={false}
            beforeUpload={(file) => {
              importMixFile(file);
              return false;
            }}
          >
            <Button size="small" icon={<DownloadOutlined />}>
              Import
            </Button>
          </Upload>
          <Button
            size="small"
            icon={<UploadOutlined />}
            onClick={exportMix}
          >
            Export
          </Button>
        </div>
      )}
    >
      <PlaygroundBlock title="Beer">
        <div className="pricing-example-hero">
          <div className="pricing-example-search">
            <Select
              showSearch
              allowClear
              placeholder="Search beers"
              optionFilterProp="label"
              loading={productsLoading}
              value={selectedProductId}
              onChange={onBeerChange}
              options={productOptions}
              className="pricing-example-beer-select"
              style={{ width: '100%' }}
            />
          </div>
          {selectedProduct ? (
            <div className="pricing-example-identity">
              <Avatar
                size={56}
                src={selectedProduct.tapBadgeUrl}
                alt={selectedProduct.name}
              >
                {(selectedProduct.name || '?').slice(0, 1)}
              </Avatar>
              <div className="pricing-example-identity-meta">
                <Text strong className="pricing-example-beer-name">
                  {selectedProduct.name}
                </Text>
                <div className="pricing-example-sku-tags">
                  {skusLoading && (
                    <Text type="secondary">Loading SKUs…</Text>
                  )}
                  {!skusLoading && beerSkus.length === 0 && (
                    <Text type="secondary">No SKUs yet</Text>
                  )}
                  {!skusLoading && beerSkus.map((s) => (
                    <Tag key={s.key}>
                      {skuDisplayName(s, selectedProduct.name)}
                    </Tag>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </PlaygroundBlock>

      <PlaygroundBlock title="Details">
        <div className="pricing-example-specs">
          <div className="pricing-example-spec">
            <Text type="secondary" className="pricing-example-spec-label">
              ABV
            </Text>
            <InputNumber
              min={0}
              max={20}
              step={0.1}
              value={abv}
              onChange={(v) => setAbv(v)}
              style={{ width: '100%' }}
            />
          </div>
          <div className="pricing-example-spec">
            <Text type="secondary" className="pricing-example-spec-label">
              Range
            </Text>
            <Select
              value={range}
              onChange={setRange}
              className="pricing-example-spec-select"
              style={{ width: '100%' }}
              options={MARGIN_TARGET_GUIDE_RANGES.map((r) => ({
                value: r.value,
                label: r.label,
              }))}
            />
          </div>
          <div className="pricing-example-spec">
            <Text type="secondary" className="pricing-example-spec-label">
              Type
            </Text>
            <Select
              value={productType}
              onChange={setProductType}
              className="pricing-example-spec-select"
              style={{ width: '100%' }}
              options={TYPE_OPTIONS}
            />
          </div>
          <div className="pricing-example-spec pricing-example-spec-cost">
            <Tooltip title="Ingredient cost only — wages, excise, keg lease and 3PL are added for kegs">
              <Text type="secondary" className="pricing-example-spec-label">
                Cost/L (ingredients)
              </Text>
            </Tooltip>
            <Input
              prefix="$"
              placeholder="Optional"
              value={costPerLitreText}
              onChange={onCostPerLitreChange}
              style={{ width: '100%' }}
              inputMode="decimal"
            />
          </div>
        </div>
      </PlaygroundBlock>

      <PlaygroundBlock
        className="pricing-example-block-sku"
        title={`SKU · ${preset.label}`}
      >
        <div className="pricing-example-presets">
          {EXAMPLE_FORMAT_PRESET_GROUPS.map((group, groupIndex) => (
            <React.Fragment key={group.key}>
              {groupIndex > 0 && (
                <span className="pricing-example-preset-divider" aria-hidden />
              )}
              {group.presets.map((p) => {
                const exists = existingFormatKeys.has(p.key);
                return (
                  <Tooltip
                    key={p.key}
                    title={exists
                      ? 'Existing SKU — Apply will update prices'
                      : undefined}
                  >
                    <Button
                      type={p.key === formatKey ? 'primary' : 'default'}
                      className={exists ? 'pricing-example-preset-exists' : ''}
                      onClick={() => applyPreset(p)}
                    >
                      {p.label}
                    </Button>
                  </Tooltip>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        {costBreakdown && (
          <div className="pricing-example-cost-breakdown-wrap">
            <Text type="secondary" className="pricing-example-cost-breakdown">
              {costBreakdown}
            </Text>
            <Popover
              trigger="click"
              placement="bottomLeft"
              overlayClassName="pricing-example-cost-popover"
              title="Edit costs — this session only"
              content={(
                <CostEditor
                  sku={sku}
                  costOverrides={costOverrides}
                  packagingOverrides={packagingOverrides}
                  onRateChange={onCostRateChange}
                  onRateReset={onCostRateReset}
                  onPackagingChange={onPackagingRateChange}
                  onPackagingReset={onPackagingRateReset}
                  onResetAll={resetAllCostOverrides}
                  hasOverrides={hasCostOverrides}
                />
              )}
            >
              <Tooltip title="Edit cost inputs">
                <button
                  type="button"
                  className={[
                    'pricing-example-cost-edit',
                    hasCostOverrides ? 'pricing-example-cost-edit-on' : '',
                  ].filter(Boolean).join(' ')}
                  aria-label="Edit cost inputs"
                >
                  <EditOutlined />
                </button>
              </Tooltip>
            </Popover>
          </div>
        )}
        <div className="pricing-example-sku-prices">
          <Row gutter={[16, 16]} className="pricing-example-groups">
            {groups.map((group, groupIndex) => (
              <Col xs={24} md={8} key={group.title}>
                <div className="pricing-example-group-title">{group.title}</div>
                {group.fields.map((field) => {
                  const greyed = exampleFieldGreyed(sku, field.key);
                  const skip = solved.skipped[field.key];
                  const value = skip ? null : solved.prices[field.key];
                  const isAnchor = field.key === anchorField;
                  const isLocked = field.key === lockedField;
                  const lockTip = isLocked
                    ? 'Unlock — other sliders keep this value'
                    : 'Lock this price';
                  const note = withCurrentNote(
                    fieldNote({
                      fieldKey: field.key,
                      sku,
                      prices: solved.prices,
                      targetMap,
                      rules,
                      abv,
                      skipped: solved.skipped,
                      kegProduction,
                      packagedProduction,
                    }),
                    existingSku,
                    field.key,
                  );
                  const greyTip = greyed
                    ? priceFieldGreyOutTooltip(sku, field.key)
                      || 'Does not apply to this format'
                    : undefined;
                  const disabled = greyed || !!skip;
                  const displayValue = disabled ? null : value;
                  const slide = sliderRange(
                    field.key,
                    displayValue,
                    isKegSku(sku),
                  );
                  const sliderClass = [
                    'pricing-example-slider',
                    isAnchor && !isLocked ? 'pricing-example-slider-anchor' : '',
                    isLocked ? 'pricing-example-slider-locked' : '',
                  ].filter(Boolean).join(' ');
                  const controls = (
                    <div className="pricing-example-controls">
                      <InputNumber
                        size="middle"
                        disabled={disabled}
                        className="pricing-example-input"
                        value={displayValue}
                        min={0}
                        step={0.5}
                        formatter={(v) => {
                          if (v === '' || v == null) return '';
                          return `$${formatMoney(v)}`;
                        }}
                        parser={(v) => String(v).replace(/\$/g, '')}
                        onChange={(v) => onFieldChange(field.key, v)}
                      />
                      <div
                        className={sliderClass}
                        role="presentation"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          lockPageTextSelect();
                        }}
                      >
                        <Slider
                          disabled={disabled}
                          min={slide.min}
                          max={slide.max}
                          step={0.5}
                          value={
                            displayValue == null ? slide.min : displayValue
                          }
                          onChange={(v) => onFieldChange(field.key, v)}
                          tipFormatter={(v) => `$${formatMoney(v)}`}
                        />
                      </div>
                    </div>
                  );
                  return (
                    <div key={field.key} className="pricing-example-row">
                      <div className="pricing-example-label">
                        {isAnchor && (
                          <span className="pricing-example-lead-dot" aria-hidden />
                        )}
                        <Text className="pricing-example-label-text">
                          {field.label}
                        </Text>
                        {!disabled && (
                          <Tooltip title={lockTip}>
                            <button
                              type="button"
                              className={[
                                'pricing-example-lock',
                                isLocked ? 'pricing-example-lock-on' : '',
                              ].filter(Boolean).join(' ')}
                              onClick={() => toggleLock(field.key, value)}
                              aria-pressed={isLocked}
                              aria-label={lockTip}
                            >
                              {isLocked ? <LockOutlined /> : <UnlockOutlined />}
                            </button>
                          </Tooltip>
                        )}
                      </div>
                      {greyed ? (
                        <Tooltip title={greyTip}>{controls}</Tooltip>
                      ) : controls}
                      <Text type="secondary" className="pricing-example-note">
                        {note || '\u00a0'}
                      </Text>
                    </div>
                  );
                })}
                {groupIndex === 2 && applyRow}
              </Col>
            ))}
            {groups.length < 3 && (
              <Col xs={24} md={8}>
                <div className="pricing-example-group-title">{'\u00a0'}</div>
                {applyRow}
              </Col>
            )}
          </Row>
        </div>
      </PlaygroundBlock>
      <Modal
        title="Apply prices"
        visible={applyOpen}
        onCancel={() => setApplyOpen(false)}
        onOk={confirmApply}
        okText={existingSku ? 'Update prices' : 'Create SKU'}
        confirmLoading={applying}
        width={840}
        destroyOnClose
        className="pricing-example pricing-example-apply-modal"
      >
        <PlaygroundBlock title="Beer">
          {selectedProduct && (
            <div className="pricing-example-identity">
              <Avatar
                size={56}
                src={selectedProduct.tapBadgeUrl}
                alt={selectedProduct.name}
              >
                {(selectedProduct.name || '?').slice(0, 1)}
              </Avatar>
              <div className="pricing-example-identity-meta">
                <Text strong className="pricing-example-beer-name">
                  {selectedProduct.name}
                </Text>
              </div>
            </div>
          )}
        </PlaygroundBlock>
        <PlaygroundBlock
          className="pricing-example-block-sku"
          title={`SKU · ${preset.label}`}
          extra={(
            <Button
              size="small"
              onClick={() => {
                setApplyPrices((prev) => roundApplyPricesTo50c(prev));
              }}
            >
              Round to 50¢
            </Button>
          )}
        >
          <Row gutter={[16, 16]}>
            {applyGroups.map((group) => (
              <Col xs={24} md={8} key={group.title}>
                <div className="pricing-example-group-title">{group.title}</div>
                {group.fields.map((field) => {
                  const greyed = exampleFieldGreyed(sku, field.key);
                  const skip = solved.skipped[field.key];
                  const disabled = greyed || !!skip;
                  const value = disabled ? null : applyPrices[field.key];
                  const gp = disabled
                    ? null
                    : withCurrentNote(
                      applyFieldNote(field.key, sku, applyPrices),
                      existingSku,
                      field.key,
                    );
                  return (
                    <div key={field.key} className="pricing-example-row">
                      <div className="pricing-example-label">
                        <Text className="pricing-example-label-text">
                          {field.label}
                        </Text>
                      </div>
                      <InputNumber
                        size="middle"
                        disabled={disabled}
                        className="pricing-example-apply-input"
                        value={value}
                        min={0}
                        step={0.5}
                        formatter={(v) => {
                          if (v === '' || v == null) return '';
                          return `$${formatMoney(v)}`;
                        }}
                        parser={(v) => String(v).replace(/\$/g, '')}
                        onChange={(v) => {
                          setApplyPrices((prev) => ({
                            ...prev,
                            [field.key]: v,
                          }));
                        }}
                      />
                      <Text type="secondary" className="pricing-example-note">
                        {gp || '\u00a0'}
                      </Text>
                    </div>
                  );
                })}
              </Col>
            ))}
          </Row>
        </PlaygroundBlock>
      </Modal>
    </Card>
  );
};

PricingExample.propTypes = {
  targetMap: PropTypes.objectOf(PropTypes.shape({
    pct: PropTypes.number,
    rounding: PropTypes.string,
  })),
  rules: PropTypes.objectOf(PropTypes.string),
};

PricingExample.defaultProps = {
  targetMap: {},
  rules: {},
};

export default PricingExample;
